import type { Chat, PublicUser, SharePreview, Space, SpaceInvitePreview, SpaceMember } from "@rhema/client";
import {
  Avatar,
  Badge,
  BellIcon,
  Button,
  ChevronLeftIcon,
  cn,
  EmptyState,
  focusRing,
  HashIcon,
  IconButton,
  LayersIcon,
  LinkIcon,
  LockIcon,
  type MenuItem,
  PageHeader,
  PenIcon,
  PlusIcon,
  RailSlot,
  SearchIcon,
  SlidersIcon,
  StarIcon,
  TextField,
  UiLink,
  UserPlusIcon,
  UsersIcon,
} from "@rhema/ui";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useFailure } from "../billing/UpgradeLink";
import { draftPreview, useDrafts } from "../conversation/drafts";
import { initialsFor, isUnread, statusLine, toneFor } from "../data/view";
import { useMenus } from "../menus/actions";
import { useItemMenu } from "../menus/useItemMenu";
import { MoreButton, WithMenu } from "../menus/WithMenu";
import { PhotoPicker } from "../people/PhotoPicker";
import { onContactsChanged } from "../people/useContacts";
import { useApi, useClient, useClientState, useMe } from "../session/session";
import {
  channelsOf,
  homeSpaceId,
  isAdmin,
  isForeign,
  isGuest,
  isGuestIn,
  sharedNote,
  spaceActivity,
  spaceHref,
  spaceOf,
} from "./data";
import {
  BrowseChannelsSheet,
  CreateChannelSheet,
  CreateSpaceSheet,
  GuestInviteSheet,
  JoinSpaceSheet,
  SpaceInviteSheet,
  SpaceSettingsSheet,
} from "./SpaceSheets";

/** The title row of a person's phone menu: their photo, name and a detail such as their role. */
function personHeading(user: PublicUser, detail: string | undefined) {
  return {
    title: user.display_name,
    detail,
    icon: <Avatar name={user.display_name} src={user.avatar_url} tone={toneFor(user.id)} size={44} />,
  };
}

/** A space's mark: its initials on a rounded tile. */
export function SpaceMark({
  space,
  size,
}: {
  space: Pick<Space, "id" | "name"> & { avatar_url?: string | null };
  size: number;
}) {
  return (
    <Avatar
      name={space.name}
      src={space.avatar_url}
      initials={initialsFor(space.name)}
      tone={toneFor(space.id)}
      size={size}
      shape="rounded"
      serif
    />
  );
}

/**
 * A space's logo inside its rail tile. An uploaded logo shows on its own on the white tile, with no
 * initials painted under it. Spaces without one, or whose logo cannot load, show their initials tile.
 */
// The spoken label for a mention count, such as "1 mention" or "3 mentions".
function mentionLabel(count: number) {
  return count === 1 ? "1 mention" : `${count} mentions`;
}

function RailSpaceLogo({ space }: { space: Pick<Space, "id" | "name"> & { avatar_url?: string | null } }) {
  const [broken, setBroken] = useState<string | null>(null);
  const src = space.avatar_url && broken !== space.avatar_url ? space.avatar_url : null;
  if (src) {
    return (
      <img
        src={src}
        alt=""
        draggable={false}
        onError={() => setBroken(src)}
        className="h-[34px] w-[34px] rounded-lg object-contain"
      />
    );
  }
  return <SpaceMark space={{ id: space.id, name: space.name }} size={48} />;
}

/**
 * The spaces in the desktop rail, under the divider. The selected space has a blue bar on the
 * rail's edge and a blue outline. Unread shows a short bar, and mentions a gold count.
 */
export function SpaceTiles({ activeId }: { activeId?: string }) {
  const state = useClientState();
  const menus = useMenus();
  return (
    <div className="flex w-full flex-col items-center gap-2">
      {state.spaces.map((space) => {
        const { unread, mentions } = spaceActivity(state, space.id);
        const active = space.id === activeId;
        return (
          <WithMenu
            key={space.id}
            as="div"
            className="w-full"
            label={space.name}
            items={() => menus.space(space)}
            heading={{ title: space.name, detail: "Space", icon: <SpaceMark space={space} size={44} /> }}
            more={false}
          >
            <RailSlot active={active} unread={unread && mentions === 0} hoverMark>
              <UiLink
                href={spaceHref(space.id)}
                aria-label={space.name}
                aria-current={active ? "page" : undefined}
                title={space.name}
                className={cn(
                  "relative flex h-12 w-12 flex-none items-center justify-center rounded-tile bg-surface",
                  active ? "ring-2 ring-blue" : "ring-1 ring-hairline hover:ring-line-strong",
                  focusRing,
                  "focus-visible:outline-offset-4",
                )}
              >
                <RailSpaceLogo space={space} />
                {mentions > 0 && (
                  <Badge
                    variant="unread"
                    count={mentions}
                    size="sm"
                    label={mentionLabel(mentions)}
                    ringClassName="border-surface-header"
                    className="absolute -top-1.5 -right-1.5"
                  />
                )}
              </UiLink>
            </RailSlot>
          </WithMenu>
        );
      })}
      <UiLink
        href="/spaces"
        aria-label="Create or join a space"
        title="Create or join a space"
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-tile border border-dashed border-line-strong text-ink-secondary hover:bg-surface-low",
          focusRing,
        )}
      >
        <PlusIcon size={20} />
      </UiLink>
    </div>
  );
}

/** The spaces you are in, with creating and joining. Phones open it from the Spaces tab. */
export function SpacesHome({ variant }: { variant: "phone" | "desktop" }) {
  const state = useClientState();
  const menus = useMenus();
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button icon={<PlusIcon size={17} />} onClick={() => setCreating(true)}>
        Create a space
      </Button>
      <Button variant="outline" onClick={() => setJoining(true)}>
        Join with a link
      </Button>
    </div>
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className={variant === "desktop" ? "mx-auto w-full max-w-[640px] pb-10" : "pb-24"}>
        <PageHeader title="Spaces" size={variant === "desktop" ? "md" : "lg"} />
        <div className="flex flex-col gap-4 px-4">
          {state.spaces.length === 0 ? (
            <EmptyState icon={<LayersIcon size={26} />} title="No spaces yet" action={actions}>
              A space holds channels for a team, a church or a club, with threads for each conversation. Your
              chats stay as they are.
            </EmptyState>
          ) : (
            <>
              <ul aria-label="Your spaces" className="m-0 flex list-none flex-col gap-2 p-0">
                {state.spaces.map((space) => {
                  const { unread, mentions } = spaceActivity(state, space.id);
                  return (
                    <WithMenu
                      key={space.id}
                      label={space.name}
                      items={() => menus.space(space)}
                      heading={{
                        title: space.name,
                        detail: "Space",
                        icon: <SpaceMark space={space} size={44} />,
                      }}
                      buttonClassName="right-4"
                    >
                      <UiLink
                        href={spaceHref(space.id)}
                        className={cn(
                          "flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-3 text-ink no-underline hover:bg-surface-header/60",
                          focusRing,
                        )}
                      >
                        <SpaceMark space={space} size={46} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className={cn("truncate text-base", unread ? "font-bold" : "font-semibold")}>
                            {space.name}
                          </span>
                          <span className="truncate text-[13px] text-ink-secondary">
                            {space.member_count} {space.member_count === 1 ? "member" : "members"}
                          </span>
                        </span>
                        {mentions > 0 && (
                          <Badge variant="unread" count={mentions} label={mentionLabel(mentions)} />
                        )}
                      </UiLink>
                    </WithMenu>
                  );
                })}
              </ul>
              {actions}
            </>
          )}
        </div>
      </div>
      <CreateSpaceSheet open={creating} onClose={() => setCreating(false)} />
      <JoinSpaceSheet open={joining} onClose={() => setJoining(false)} />
    </div>
  );
}

function ChannelRow({ chat, spaceId, selected }: { chat: Chat; spaceId: string; selected: boolean }) {
  const unread = isUnread(chat);
  const markedUnread = chat.unread === 0 && chat.marked_unread === true;
  const menus = useMenus();
  const space = spaceOf(useClientState(), spaceId);
  const shared = sharedNote(chat, spaceId);
  // A channel you started writing in, and have not sent to, says so until you open it again.
  const drafts = useDrafts();
  const draft = selected ? undefined : draftPreview(drafts.get(chat.id));
  return (
    <WithMenu
      label={`#${chat.title}`}
      items={() => menus.channel(chat, space)}
      heading={{
        title: `#${chat.title}`,
        detail: space ? `Channel in ${space.name}` : "Channel",
        icon: (
          <Avatar
            name={chat.title}
            src={chat.avatar_url}
            initials={initialsFor(chat.title)}
            tone={toneFor(chat.id)}
            size={44}
            shape="rounded"
          />
        ),
      }}
      buttonClassName="right-1.5 h-7 w-7"
    >
      <UiLink
        href={spaceHref(spaceId, chat.id)}
        aria-current={selected ? "page" : undefined}
        className={cn(
          "relative flex items-center gap-2.5 rounded-field px-3 py-2 text-[15px] no-underline",
          selected
            ? "bg-surface font-semibold text-blue before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-r-full before:bg-blue"
            : "text-ink-secondary hover:bg-surface-header",
          unread && !selected && "font-bold text-ink",
          focusRing,
        )}
      >
        <span className="relative flex-none">
          <Avatar
            name={chat.title}
            src={chat.avatar_url}
            initials={initialsFor(chat.title)}
            tone={toneFor(chat.id)}
            size={24}
            shape="rounded"
          />
          {chat.private && (
            <span className="absolute -right-1 -bottom-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-linen text-ink-secondary">
              <LockIcon size={9} strokeWidth={2.6} label="Private" />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1 truncate">{chat.title}</span>
        {draft && (
          <span
            title={`Draft: ${draft}`}
            className="flex flex-none items-center gap-1 text-[12.5px] font-semibold text-blue"
          >
            <PenIcon size={13} strokeWidth={2.4} />
            Draft
          </span>
        )}
        {shared && (
          <span title={shared} className="flex flex-none text-ink-meta">
            <LinkIcon size={13} label={shared} />
          </span>
        )}
        {chat.mentioned && <Badge variant="mention" label="You were mentioned" />}
        {markedUnread && !chat.mentioned && (
          <span
            role="img"
            aria-label="Marked as unread"
            className="mr-1 h-2.5 w-2.5 flex-none rounded-full bg-gold"
          />
        )}
      </UiLink>
    </WithMenu>
  );
}

/** A space's channels, with its invite, members and settings. The list column on desktop. */
export function SpaceSidebar({
  spaceId,
  selectedId,
  variant,
}: {
  spaceId: string;
  selectedId?: string;
  variant: "phone" | "desktop";
}) {
  const state = useClientState();
  const menus = useMenus();
  const [params, setParams] = useSearchParams();
  const space = spaceOf(state, spaceId);
  const [sheet, setSheet] = useState<"invite" | "guest" | "browse" | "create" | "settings" | null>(null);

  // A new space opens with its invite link ready to share. Menus elsewhere open a sheet the same way.
  useEffect(() => {
    if (!space) return;
    const open = (["invite", "guest", "settings"] as const).find((k) => params.get(k) === "1");
    if (!open) return;
    setSheet(open);
    const p = new URLSearchParams(params);
    p.delete("invite");
    p.delete("guest");
    p.delete("settings");
    setParams(p, { replace: true });
  }, [params, setParams, space]);

  if (!space) {
    return (
      <EmptyState
        icon={<LayersIcon size={26} />}
        title={state.spaces.length ? "Space not found" : "Opening the space"}
        className="flex-1"
      >
        {state.spaces.length ? "You may have left this space, or it was deleted." : undefined}
      </EmptyState>
    );
  }
  const channels = channelsOf(state, space.id);
  const starred = channels.filter((c) => c.starred);
  const rest = channels.filter((c) => !c.starred);
  const admin = isAdmin(space);
  const guest = isGuest(space);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-linen">
      <header className="flex flex-none items-start gap-3 px-5 pt-[22px] pb-3">
        {variant === "phone" && (
          <UiLink
            href="/spaces"
            aria-label="Back to spaces"
            className={cn("-ml-2 mt-1 rounded-full p-1 text-ink", focusRing)}
          >
            <ChevronLeftIcon size={24} />
          </UiLink>
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <h1 className="m-0 line-clamp-2 font-serif text-[26px] leading-tight font-medium tracking-[-0.02em] [overflow-wrap:anywhere]">
            {space.name}
          </h1>
          <UiLink
            href={`/spaces/${space.id}/members`}
            className={cn(
              "self-start rounded text-[13px] text-ink-secondary no-underline hover:text-ink",
              focusRing,
            )}
          >
            {guest
              ? "You are a guest"
              : `${space.member_count} ${space.member_count === 1 ? "member" : "members"}`}
          </UiLink>
        </span>
        <span className="mt-1 flex flex-none gap-1">
          {admin && (
            <IconButton label="Invite people" variant="outline" size={38} onClick={() => setSheet("invite")}>
              <UserPlusIcon size={17} />
            </IconButton>
          )}
          <IconButton label="Space settings" variant="ghost" size={38} onClick={() => setSheet("settings")}>
            <SlidersIcon size={18} />
          </IconButton>
          <MoreButton
            label={space.name}
            items={() => menus.space(space)}
            heading={{ title: space.name, detail: "Space", icon: <SpaceMark space={space} size={44} /> }}
            className="h-[38px] w-[38px]"
          />
        </span>
      </header>

      <nav aria-label={`Channels in ${space.name}`} className="min-h-0 flex-1 overflow-y-auto px-3 pb-24">
        {starred.length > 0 && (
          <>
            <h2 className="m-0 flex items-center gap-1.5 px-3 pt-2 pb-1.5 text-[13px] font-semibold text-ink-secondary">
              <StarIcon size={13} strokeWidth={2.4} />
              Starred
            </h2>
            <ul aria-label="Starred channels" className="m-0 mb-2 flex list-none flex-col gap-0.5 p-0">
              {starred.map((c) => (
                <ChannelRow key={c.id} chat={c} spaceId={space.id} selected={c.id === selectedId} />
              ))}
            </ul>
          </>
        )}
        <h2 className="m-0 px-3 pt-2 pb-1.5 text-[13px] font-semibold text-ink-secondary">Channels</h2>
        <ul aria-label="Channels" className="m-0 flex list-none flex-col gap-0.5 p-0">
          {rest.map((c) => (
            <ChannelRow key={c.id} chat={c} spaceId={space.id} selected={c.id === selectedId} />
          ))}
        </ul>
        {guest && (
          <p className="m-0 px-3 pt-2 text-[13px] leading-normal text-ink-meta">
            Guests see only the channels they were invited to.
          </p>
        )}
        <div className="mt-2 flex flex-col gap-0.5">
          {!guest && (
            <button
              type="button"
              onClick={() => setSheet("browse")}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-field border-0 bg-transparent px-3 py-2 text-left text-[15px] text-ink-secondary hover:bg-surface-header",
                focusRing,
              )}
            >
              <SearchIcon size={15} />
              Browse channels
            </button>
          )}
          {!guest && (
            <button
              type="button"
              onClick={() => setSheet("create")}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-field border-0 bg-transparent px-3 py-2 text-left text-[15px] text-ink-secondary hover:bg-surface-header",
                focusRing,
              )}
            >
              <PlusIcon size={15} />
              Create a channel
            </button>
          )}
          <UiLink
            href={`/spaces/${space.id}/members`}
            className={cn(
              "flex items-center gap-2.5 rounded-field px-3 py-2 text-[15px] text-ink-secondary no-underline hover:bg-surface-header",
              focusRing,
            )}
          >
            <UsersIcon size={15} />
            Members
          </UiLink>
          {admin && (
            <button
              type="button"
              onClick={() => setSheet("guest")}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-field border-0 bg-transparent px-3 py-2 text-left text-[15px] text-ink-secondary hover:bg-surface-header",
                focusRing,
              )}
            >
              <UserPlusIcon size={15} />
              Invite a guest
            </button>
          )}
          {admin && (
            <UiLink
              href={`/spaces/${space.id}/manage`}
              className={cn(
                "flex items-center gap-2.5 rounded-field px-3 py-2 text-[15px] text-ink-secondary no-underline hover:bg-surface-header",
                focusRing,
              )}
            >
              <SlidersIcon size={15} />
              Manage space
            </UiLink>
          )}
        </div>
      </nav>

      <SpaceInviteSheet space={space} open={sheet === "invite"} onClose={() => setSheet(null)} />
      <GuestInviteSheet space={space} open={sheet === "guest"} onClose={() => setSheet(null)} />
      <BrowseChannelsSheet
        space={space}
        open={sheet === "browse"}
        onClose={() => setSheet(null)}
        onCreate={() => setSheet("create")}
      />
      <CreateChannelSheet space={space} open={sheet === "create"} onClose={() => setSheet(null)} />
      <SpaceSettingsSheet space={space} open={sheet === "settings"} onClose={() => setSheet(null)} />
    </div>
  );
}

const ROLE_NAMES = { owner: "Owner", admin: "Admin", member: "Member", guest: "Guest" } as const;

/**
 * The quick buttons on a member's row for admins: their role change and Remove. Each runs the
 * menu's own item, so the words and the confirmation are the same either way.
 */
function MemberButtons({ items }: { items: MenuItem[] }) {
  const find = (...ids: string[]) =>
    items.find(
      (i): i is Extract<MenuItem, { onSelect: () => void }> => "onSelect" in i && ids.includes(i.id),
    );
  const role = find("promote", "demote", "member");
  const remove = find("remove");
  if (!role && !remove) return null;
  return (
    <span className="flex flex-wrap justify-end gap-1.5">
      {role && (
        <Button variant="outline" size="sm" onClick={role.onSelect}>
          {role.label}
        </Button>
      )}
      {remove && (
        <Button variant="ghost" size="sm" className="text-scarlet" onClick={remove.onSelect}>
          Remove
        </Button>
      )}
    </span>
  );
}

/** Everyone in the space, with roles. Admins can make admins and remove people. */
export function SpaceMembers({ spaceId, variant }: { spaceId: string; variant: "phone" | "desktop" }) {
  const api = useApi();
  const menus = useMenus();
  const client = useClient();
  const me = useMe();
  const state = useClientState();
  const space = spaceOf(state, spaceId);
  const [members, setMembers] = useState<SpaceMember[] | null>(null);
  const [inviting, setInviting] = useState<"member" | "guest" | null>(null);
  const { error, fail, clear } = useFailure();

  const load = () =>
    api
      .spaceMembers(spaceId)
      .then(setMembers)
      .catch((err) => fail(err));
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloads when the space changes
  useEffect(() => {
    void load();
  }, [api, spaceId, space?.member_count]);
  // Blocking someone from any menu shows here at once.
  // biome-ignore lint/correctness/useExhaustiveDependencies: subscribes once per space
  useEffect(() => onContactsChanged(() => void load()), [spaceId]);

  if (!space) return <EmptyState title="Space not found" className="flex-1" />;
  const admin = isAdmin(space);
  const canManage = (m: SpaceMember) =>
    admin && m.user.id !== me.id && m.role !== "owner" && (m.role !== "admin" || space.role === "owner");
  const itemsFor = (m: SpaceMember) =>
    menus.person(m.user, {
      role: m.role,
      where: "space",
      onChanged: () => load(),
      ...(canManage(m)
        ? {
            setRole: (role: "admin" | "member") => change(() => api.setSpaceRole(space.id, m.user.id, role)),
            remove: () => change(() => api.removeFromSpace(space.id, m.user.id)),
          }
        : {}),
    });

  /** Changes someone's place in the space, then reloads the list. Failures go to the menu's note. */
  const change = async (job: () => Promise<unknown>) => {
    clear();
    await job();
    await Promise.all([load(), client.refreshSpaces()]);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className={variant === "desktop" ? "mx-auto w-full max-w-[640px] pb-10" : "pb-24"}>
        <div className="px-4 pt-4">
          <UiLink
            href={spaceHref(space.id)}
            className={cn(
              "inline-flex items-center gap-1 rounded px-1 text-sm font-semibold text-blue no-underline",
              focusRing,
            )}
          >
            <ChevronLeftIcon size={16} strokeWidth={2.2} />
            {space.name}
          </UiLink>
        </div>
        <PageHeader
          title="Members"
          size={variant === "desktop" ? "md" : "lg"}
          className="pt-2"
          actions={
            admin ? (
              <span className="flex gap-1.5">
                <Button size="sm" icon={<UserPlusIcon size={16} />} onClick={() => setInviting("member")}>
                  Invite
                </Button>
                <Button size="sm" variant="outline" onClick={() => setInviting("guest")}>
                  Invite a guest
                </Button>
              </span>
            ) : undefined
          }
        />
        <div className="flex flex-col gap-2 px-4">
          {error && (
            <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
              {error}
            </p>
          )}
          {!members && !error && <p className="m-0 text-sm text-ink-meta">Loading the members.</p>}
          {isGuest(space) && (
            <p className="m-0 text-sm leading-normal text-ink-secondary">
              As a guest you see the people in your channels.
            </p>
          )}
          <ul
            aria-label="Members"
            className="m-0 flex list-none flex-col rounded-card border border-line bg-surface p-0"
          >
            {members?.map((m) => {
              const self = m.user.id === me.id;
              return (
                <WithMenu
                  key={m.user.id}
                  label={m.user.display_name}
                  heading={personHeading(m.user, ROLE_NAMES[m.role])}
                  items={() => itemsFor(m)}
                  className={cn(
                    "flex items-center gap-3 border-t border-line py-3 pl-4 first:border-t-0",
                    self ? "pr-4" : "pr-14",
                  )}
                >
                  <Avatar
                    name={m.user.display_name}
                    src={m.user.avatar_url}
                    tone={toneFor(m.user.id)}
                    size={40}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[15px] font-semibold">
                      {self ? `${m.user.display_name} (you)` : m.user.display_name}
                    </span>
                    <span className="truncate text-[12.5px] text-ink-secondary">
                      {[ROLE_NAMES[m.role], statusLine(m.user)].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {canManage(m) && <MemberButtons items={itemsFor(m)} />}
                </WithMenu>
              );
            })}
          </ul>
        </div>
      </div>
      <SpaceInviteSheet space={space} open={inviting === "member"} onClose={() => setInviting(null)} />
      <GuestInviteSheet
        space={space}
        open={inviting === "guest"}
        onClose={() => setInviting(null)}
        onAdded={() => void load()}
      />
    </div>
  );
}

/** Opened from a space invite link, /spaces/join/<code>. */
export function SpaceJoinScreen({ code }: { code: string }) {
  const api = useApi();
  const client = useClient();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<SpaceInvitePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const { error, fail, clear } = useFailure();

  // biome-ignore lint/correctness/useExhaustiveDependencies: loads once per code
  useEffect(() => {
    api
      .previewSpaceInvite(code)
      .then(setPreview)
      .catch((err) => fail(err));
  }, [api, code]);

  const join = async () => {
    setBusy(true);
    clear();
    try {
      const space = await client.joinSpace(code);
      navigate(spaceHref(space.id), { replace: true });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (error && !preview) {
    return (
      <EmptyState icon={<LayersIcon size={26} />} title="This invite does not work" className="flex-1">
        {error}
      </EmptyState>
    );
  }
  if (!preview) return <EmptyState title="Opening the invite" className="flex-1" />;
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
      <SpaceMark space={preview} size={96} />
      <h1 className="m-0 font-serif text-[30px] font-medium tracking-[-0.02em]">{preview.name}</h1>
      {preview.about && <p className="m-0 max-w-[420px] text-[15px] text-ink-secondary">{preview.about}</p>}
      <p className="m-0 text-sm text-ink-secondary">
        {preview.member_count} {preview.member_count === 1 ? "member" : "members"}
      </p>
      {preview.guest && !preview.member && (
        <p className="m-0 max-w-[420px] text-[15px] text-ink-secondary">
          You join as a guest in {(preview.channels ?? []).map((c) => `#${c}`).join(", ") || "its channels"}.
          Guests see only the channels they are invited to.
        </p>
      )}
      {error && (
        <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
          {error}
        </p>
      )}
      {preview.member ? (
        <Button size="lg" onClick={() => navigate(spaceHref(preview.id), { replace: true })}>
          Open the space
        </Button>
      ) : (
        <Button size="lg" disabled={busy} onClick={() => void join()}>
          {busy ? "Joining" : preview.guest ? "Join as a guest" : "Join the space"}
        </Button>
      )}
    </div>
  );
}

/**
 * Opened from a shared channel link, /spaces/share/<code>. An admin of another space picks which
 * of their spaces gets the channel. The link works once.
 */
export function ShareAcceptScreen({ code }: { code: string }) {
  const api = useApi();
  const client = useClient();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<SharePreview | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { error, fail, clear } = useFailure();

  // biome-ignore lint/correctness/useExhaustiveDependencies: loads once per code
  useEffect(() => {
    api
      .previewShare(code)
      .then((p) => {
        setPreview(p);
        setTarget(p.spaces[0]?.id ?? null);
      })
      .catch((err) => fail(err));
  }, [api, code]);

  const accept = async () => {
    if (!target) return;
    setBusy(true);
    clear();
    try {
      const chat = await client.acceptShare(code, target);
      navigate(spaceHref(target, chat.id), { replace: true });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (error && !preview) {
    return (
      <EmptyState icon={<LinkIcon size={26} />} title="This link does not work" className="flex-1">
        {error}
      </EmptyState>
    );
  }
  if (!preview) return <EmptyState title="Opening the link" className="flex-1" />;
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 overflow-y-auto px-6 py-8 text-center">
      <SpaceMark
        space={{ id: preview.space.id, name: preview.space.name, avatar_url: preview.space_avatar_url }}
        size={80}
      />
      <h1 className="m-0 flex items-center gap-2 font-serif text-[28px] font-medium tracking-[-0.02em]">
        {preview.private ? <LockIcon size={22} /> : <HashIcon size={22} />}
        {preview.name}
      </h1>
      <p className="m-0 max-w-[440px] text-[15px] text-ink-secondary">
        {preview.space.name} wants to share this channel with a space you look after. People in both spaces
        can talk in it. {preview.space.name} manages it, and you can take it out of your space at any time.
      </p>
      {preview.topic && <p className="m-0 max-w-[440px] text-sm text-ink-meta">{preview.topic}</p>}
      {preview.spaces.length === 0 ? (
        <p className="m-0 max-w-[440px] text-sm font-semibold">
          You need to be an admin of another space to add this channel to it.
        </p>
      ) : (
        <fieldset className="m-0 flex w-full max-w-[400px] flex-col gap-1.5 border-0 p-0 text-left">
          <legend className="mb-1.5 text-[15px] font-bold">Add it to</legend>
          {preview.spaces.map((s) => (
            <label
              key={s.id}
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-field border px-3.5 py-2.5",
                target === s.id ? "border-blue bg-blue-tint" : "border-line bg-surface",
              )}
            >
              <input
                type="radio"
                name="space"
                value={s.id}
                checked={target === s.id}
                onChange={() => setTarget(s.id)}
                className="accent-blue"
              />
              <SpaceMark space={s} size={30} />
              <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{s.name}</span>
            </label>
          ))}
        </fieldset>
      )}
      {error && (
        <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
          {error}
        </p>
      )}
      {preview.spaces.length > 0 && (
        <Button size="lg" disabled={busy || !target} onClick={() => void accept()}>
          {busy ? "Adding the channel" : "Add the channel"}
        </Button>
      )}
    </div>
  );
}

/** What a channel's notifications are, and where they come from. */
function notifyWords(chat: Chat, space: Space): string {
  const muted = Boolean(chat.muted_until && new Date(chat.muted_until).getTime() > Date.now());
  const what = muted ? "Muted" : chat.mentions_only ? "Mentions only" : "All messages";
  return chat.notify_follows_space
    ? `${what}. Follows your setting for ${space.name}.`
    : `${what}. Set for this channel.`;
}

/** The Notifications row of a channel's info, which opens the same choices as its menu. */
function NotificationsRow({ chat, space, items }: { chat: Chat; space: Space; items: MenuItem[] }) {
  const menu = useItemMenu(`Notifications for #${chat.title}`, () => items, {
    title: "Notifications",
    detail: `#${chat.title}`,
  });
  return (
    <div className="flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-3">
      <BellIcon size={18} className="flex-none text-ink-secondary" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[15px] font-semibold">Notifications</span>
        <span className="text-[12.5px] text-ink-secondary">{notifyWords(chat, space)}</span>
      </span>
      <Button
        variant="outline"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={(e) => menu.openFrom(e.currentTarget)}
      >
        Change
      </Button>
      {menu.menu}
    </div>
  );
}

/** A channel's name, topic and people, with notifications and leaving. */
export function ChannelInfo({
  chatId,
  spaceId,
  onClose,
  variant,
}: {
  chatId: string;
  /** The space it is open in, which for a shared channel may not be the one that owns it. */
  spaceId?: string;
  onClose?: () => void;
  variant: "panel" | "phone";
}) {
  const api = useApi();
  const client = useClient();
  const menus = useMenus();
  const state = useClientState();
  const [params, setParams] = useSearchParams();
  const chat = state.chats[chatId];
  const space = chat?.space_id ? spaceOf(state, spaceId || homeSpaceId(state, chat)) : undefined;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(chat?.title ?? "");
  const [topic, setTopic] = useState(chat?.about ?? "");
  const [adding, setAdding] = useState(false);
  const [everyone, setEveryone] = useState<SpaceMember[] | null>(null);
  const [busy, setBusy] = useState(false);
  const { error, fail, clear } = useFailure();

  const run = async (job: () => Promise<void>) => {
    setBusy(true);
    clear();
    try {
      await job();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const spaceForAdding = space?.id;
  async function startAdding() {
    if (!spaceForAdding) return;
    await run(async () => {
      setEveryone(await api.spaceMembers(spaceForAdding));
      setAdding(true);
    });
  }

  useEffect(() => {
    void client.refreshChat(chatId).catch(() => undefined);
  }, [client, chatId]);

  // Add people from the channel's menu opens the list of people to add.
  const addAsked = params.get("add") === "1" && Boolean(chat && space);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the menu asks for it
  useEffect(() => {
    if (!addAsked) return;
    const p = new URLSearchParams(params);
    p.delete("add");
    setParams(p, { replace: true });
    void startAdding();
  }, [addAsked]);

  if (!chat || !space) return <EmptyState title="Channel not found" className="flex-1" />;
  const foreign = isForeign(chat, space.id);
  const manages = chat.role === "owner" || chat.role === "admin" || (isAdmin(space) && !foreign);
  const guest = isGuest(space);
  const shared = sharedNote(chat, space.id);
  const inChannel = new Set(chat.members.map((m) => m.user.id));

  const save = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.updateChat(chatId, { title: name, about: topic.trim() });
      await client.refreshChat(chatId);
      setEditing(false);
    });
  };

  const canAdd = chat.can_add ?? !guest;
  const notify = menus.channelNotify(chat, space);
  const notifyItems = notify.type === "submenu" ? notify.items : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className="flex items-center justify-between px-5 pt-5">
        {variant === "phone" ? (
          <UiLink
            href={spaceHref(space.id, chat.id)}
            className={cn(
              "inline-flex items-center gap-1 rounded text-sm font-semibold text-blue no-underline",
              focusRing,
            )}
          >
            <ChevronLeftIcon size={16} strokeWidth={2.2} />#{chat.title}
          </UiLink>
        ) : (
          <span className="text-[13px] font-semibold text-ink-secondary">Channel</span>
        )}
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3 px-5 pt-2 pb-8">
        {editing ? (
          <form onSubmit={save} className="flex flex-col gap-3">
            <TextField
              label="Name"
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Topic"
              maxLength={280}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || !name.trim()}>
                Save
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-1">
            {manages ? (
              <div className="mb-2">
                <PhotoPicker
                  name={chat.title}
                  src={chat.avatar_url}
                  initials={initialsFor(chat.title)}
                  tone={toneFor(chat.id)}
                  label={`#${chat.title}`}
                  onPick={async (photo) => {
                    await api.setChatPhoto(chat.id, photo);
                    await client.refreshChat(chat.id);
                  }}
                  onRemove={async () => {
                    await api.removeChatPhoto(chat.id);
                    await client.refreshChat(chat.id);
                  }}
                />
              </div>
            ) : (
              chat.avatar_url && (
                <Avatar
                  name={chat.title}
                  src={chat.avatar_url}
                  tone={toneFor(chat.id)}
                  size={64}
                  shape="rounded"
                />
              )
            )}
            <h2 className="m-0 flex items-center gap-2 font-serif text-[26px] font-medium tracking-[-0.02em]">
              {chat.private ? <LockIcon size={20} /> : <HashIcon size={20} />}
              {chat.title}
            </h2>
            {chat.about && <p className="m-0 text-[15px] text-ink-secondary">{chat.about}</p>}
            <p className="m-0 text-[13px] text-ink-meta">
              {chat.private ? "Private channel" : "Public channel"} in {space.name}
              {chat.only_admins_post ? ". Only admins post." : ""}
            </p>
            {shared && (
              <p className="m-0 flex items-center gap-1.5 text-[13px] text-ink-meta">
                <LinkIcon size={13} />
                {shared}
              </p>
            )}
            {manages && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2 self-start"
                onClick={() => {
                  setName(chat.title);
                  setTopic(chat.about);
                  setEditing(true);
                }}
              >
                Edit name and topic
              </Button>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
            {error}
          </p>
        )}

        <NotificationsRow chat={chat} space={space} items={notifyItems} />

        <section aria-label="People in this channel" className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h3 className="m-0 text-[15px] font-bold">
              {chat.member_count} {chat.member_count === 1 ? "person" : "people"}
            </h3>
            {canAdd && (
              <Button
                variant="outline"
                size="sm"
                icon={<UserPlusIcon size={16} />}
                onClick={() => void startAdding()}
              >
                Add people
              </Button>
            )}
          </div>
          {adding && everyone && (
            <ul
              aria-label="People in the space"
              className="m-0 flex list-none flex-col rounded-card border border-line bg-surface p-0"
            >
              {everyone
                .filter((m) => !inChannel.has(m.user.id) && (m.role !== "guest" || isAdmin(space)))
                .map((m) => (
                  <li
                    key={m.user.id}
                    className="flex items-center gap-3 border-t border-line px-4 py-2.5 first:border-t-0"
                  >
                    <Avatar
                      name={m.user.display_name}
                      src={m.user.avatar_url}
                      tone={toneFor(m.user.id)}
                      size={34}
                    />
                    <span className="min-w-0 flex-1 truncate text-[15px]">{m.user.display_name}</span>
                    {m.role === "guest" && <span className="text-[12.5px] text-ink-secondary">Guest</span>}
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await api.addToChannel(space.id, chatId, [m.user.id]);
                          await client.refreshChat(chatId);
                        })
                      }
                    >
                      Add
                    </Button>
                  </li>
                ))}
              {everyone.every((m) => inChannel.has(m.user.id)) && (
                <li className="px-4 py-3 text-sm text-ink-secondary">
                  Everyone in the space is in this channel.
                </li>
              )}
            </ul>
          )}
          <ul className="m-0 flex list-none flex-col rounded-card border border-line bg-surface p-0">
            {chat.members.map((m) => (
              <WithMenu
                key={m.user.id}
                label={m.user.display_name}
                heading={personHeading(m.user, `@${m.user.handle}`)}
                items={() =>
                  menus.person(m.user, {
                    where: "channel",
                    onChanged: () => client.refreshChat(chatId),
                    ...(manages && m.role !== "owner"
                      ? {
                          removeFromChannel: {
                            channel: chat.title,
                            run: () => client.removeFromChannel(space.id, chatId, m.user.id),
                          },
                        }
                      : {}),
                  })
                }
                className="flex items-center gap-3 border-t border-line px-4 py-2.5 first:border-t-0"
              >
                <Avatar
                  name={m.user.display_name}
                  src={m.user.avatar_url}
                  tone={toneFor(m.user.id)}
                  size={34}
                />
                <span className="min-w-0 flex-1 truncate text-[15px]">{m.user.display_name}</span>
                {isGuestIn(chat, m.user.id) ? (
                  <span className="text-[12.5px] text-ink-secondary">Guest</span>
                ) : (
                  m.role !== "member" && (
                    <span className="text-[12.5px] text-ink-secondary">{ROLE_NAMES[m.role]}</span>
                  )
                )}
              </WithMenu>
            ))}
          </ul>
        </section>

        <Button variant="danger" disabled={busy} onClick={() => menus.leaveChannel(chat, space)}>
          Leave #{chat.title}
        </Button>
      </div>
    </div>
  );
}
